/**
 * Investor-Users Admin (#1138 Tag 3) — CRUD + Initial-Password-Anzeige.
 */
import { useEffect, useState } from 'react';

const API = import.meta.env.VITE_API_URL || '';

export default function InvestorUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({ email: '', vorname: '', nachname: '', role: 'shareholder' });
  const [createBusy, setCreateBusy] = useState(false);
  const [shownPassword, setShownPassword] = useState(null);
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState({});

  async function load() {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/investor/users`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setUsers(data.rows || []);
      }
    } finally { setLoading(false); }
  }

  useEffect(() => { load(); }, []);

  async function create(e) {
    e.preventDefault();
    setCreateBusy(true);
    try {
      const res = await fetch(`${API}/api/investor/users`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(createForm),
      });
      const data = await res.json();
      if (res.ok) {
        setShownPassword({ email: createForm.email, password: data.initial_password });
        setCreateForm({ email: '', vorname: '', nachname: '', role: 'shareholder' });
        setShowCreate(false);
        await load();
      } else {
        alert('Fehler: ' + (data.error || res.status));
      }
    } finally { setCreateBusy(false); }
  }

  async function startEdit(u) {
    setEditId(u.id);
    setEditForm({ vorname: u.vorname || '', nachname: u.nachname || '', role: u.role, active: u.active });
  }

  async function saveEdit(id) {
    const res = await fetch(`${API}/api/investor/users/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(editForm),
    });
    if (res.ok) { setEditId(null); await load(); }
    else { const d = await res.json(); alert('Fehler: ' + (d.error || res.status)); }
  }

  async function resetPassword(id, email) {
    if (!confirm(`Passwort für ${email} zurücksetzen?`)) return;
    const res = await fetch(`${API}/api/investor/users/${id}/reset-password`, { method: 'POST', credentials: 'include' });
    const data = await res.json();
    if (res.ok) setShownPassword({ email, password: data.password });
    else alert('Fehler: ' + (data.error || res.status));
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-lg font-semibold text-[#1A1A2E]">Benutzer-Verwaltung</h1>
        <button
          onClick={() => setShowCreate(!showCreate)}
          className="text-xs px-3 py-1.5 rounded-md bg-[#338edc] text-white font-medium hover:bg-[#2a7bc4]"
        >
          {showCreate ? 'Abbrechen' : '+ Neuer Benutzer'}
        </button>
      </div>

      {shownPassword && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
          <div className="text-xs font-semibold text-emerald-900 mb-2">Initial-Passwort für {shownPassword.email}</div>
          <div className="font-mono text-sm bg-white border border-emerald-200 rounded-md px-3 py-2 select-all">
            {shownPassword.password}
          </div>
          <div className="text-xs text-emerald-700 mt-2">Bitte sicher übermitteln und kopieren — wird nur einmal angezeigt.</div>
          <button onClick={() => setShownPassword(null)} className="mt-2 text-xs text-emerald-900 hover:underline">Schließen</button>
        </div>
      )}

      {showCreate && (
        <form onSubmit={create} className="bg-white border border-[#E5E7EB] rounded-xl p-4 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <label className="block">
              <span className="text-[11px] text-[#6B7280]">Email</span>
              <input type="email" required value={createForm.email} onChange={e => setCreateForm({ ...createForm, email: e.target.value })}
                className="block mt-0.5 w-full px-2 py-1 text-sm border border-[#E5E7EB] rounded-md" />
            </label>
            <label className="block">
              <span className="text-[11px] text-[#6B7280]">Rolle</span>
              <select value={createForm.role} onChange={e => setCreateForm({ ...createForm, role: e.target.value })}
                className="block mt-0.5 w-full px-2 py-1 text-sm border border-[#E5E7EB] rounded-md bg-white">
                <option value="shareholder">Shareholder</option>
                <option value="admin">Admin</option>
              </select>
            </label>
            <label className="block">
              <span className="text-[11px] text-[#6B7280]">Vorname</span>
              <input type="text" value={createForm.vorname} onChange={e => setCreateForm({ ...createForm, vorname: e.target.value })}
                className="block mt-0.5 w-full px-2 py-1 text-sm border border-[#E5E7EB] rounded-md" />
            </label>
            <label className="block">
              <span className="text-[11px] text-[#6B7280]">Nachname</span>
              <input type="text" value={createForm.nachname} onChange={e => setCreateForm({ ...createForm, nachname: e.target.value })}
                className="block mt-0.5 w-full px-2 py-1 text-sm border border-[#E5E7EB] rounded-md" />
            </label>
          </div>
          <button type="submit" disabled={createBusy}
            className="text-xs px-3 py-1.5 rounded-md bg-[#338edc] text-white font-medium disabled:opacity-60">
            {createBusy ? 'Erstelle…' : 'Anlegen + Passwort generieren'}
          </button>
        </form>
      )}

      <div className="bg-white border border-[#E5E7EB] rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#F8F9FC] border-b border-[#E5E7EB]">
            <tr>
              <th className="text-left px-4 py-2 text-[11px] font-medium uppercase tracking-wide text-[#6B7280]">Email</th>
              <th className="text-left px-4 py-2 text-[11px] font-medium uppercase tracking-wide text-[#6B7280]">Name</th>
              <th className="text-left px-4 py-2 text-[11px] font-medium uppercase tracking-wide text-[#6B7280]">Rolle</th>
              <th className="text-left px-4 py-2 text-[11px] font-medium uppercase tracking-wide text-[#6B7280]">Letzter Login</th>
              <th className="text-center px-4 py-2 text-[11px] font-medium uppercase tracking-wide text-[#6B7280]">Aktiv</th>
              <th className="text-right px-4 py-2 w-44"></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-xs text-[#9CA3AF]">Lädt…</td></tr>
            ) : users.map(u => {
              const isEditing = editId === u.id;
              return (
                <tr key={u.id} className="border-t border-[#F1F2F4]">
                  <td className="px-4 py-2 text-[#1A1A2E]">{u.email}</td>
                  <td className="px-4 py-2 text-xs text-[#4B5563]">
                    {isEditing ? (
                      <div className="flex gap-1">
                        <input type="text" value={editForm.vorname} onChange={e => setEditForm({ ...editForm, vorname: e.target.value })} className="w-20 px-2 py-1 text-xs border border-[#E5E7EB] rounded-md" placeholder="Vor" />
                        <input type="text" value={editForm.nachname} onChange={e => setEditForm({ ...editForm, nachname: e.target.value })} className="w-24 px-2 py-1 text-xs border border-[#E5E7EB] rounded-md" placeholder="Nach" />
                      </div>
                    ) : `${u.vorname || ''} ${u.nachname || ''}`.trim() || <span className="text-[#9CA3AF] italic">—</span>}
                  </td>
                  <td className="px-4 py-2 text-xs">
                    {isEditing ? (
                      <select value={editForm.role} onChange={e => setEditForm({ ...editForm, role: e.target.value })} className="text-xs px-2 py-1 border border-[#E5E7EB] rounded-md bg-white">
                        <option value="shareholder">Shareholder</option>
                        <option value="admin">Admin</option>
                      </select>
                    ) : (
                      <span className={`px-2 py-0.5 rounded text-[10px] font-medium ${u.role === 'admin' ? 'bg-[#338edc]/10 text-[#338edc]' : 'bg-[#F1F2F4] text-[#4B5563]'}`}>{u.role}</span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-xs text-[#6B7280]">
                    {u.last_login_at ? new Date(u.last_login_at).toLocaleString('de-AT', { dateStyle: 'short', timeStyle: 'short' }) : <span className="italic text-[#9CA3AF]">nie</span>}
                  </td>
                  <td className="px-4 py-2 text-center">
                    {isEditing ? (
                      <input type="checkbox" checked={editForm.active} onChange={e => setEditForm({ ...editForm, active: e.target.checked })} />
                    ) : (
                      <span className={`text-xs ${u.active ? 'text-emerald-600' : 'text-red-600'}`}>{u.active ? '✓' : '×'}</span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-right">
                    {isEditing ? (
                      <div className="flex items-center gap-1 justify-end">
                        <button onClick={() => saveEdit(u.id)} className="text-xs px-2 py-1 rounded-md bg-[#338edc] text-white">OK</button>
                        <button onClick={() => setEditId(null)} className="text-xs px-2 py-1 rounded-md text-[#6B7280]">×</button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 justify-end">
                        <button onClick={() => startEdit(u)} className="text-xs text-[#338edc] hover:underline">Bearbeiten</button>
                        <button onClick={() => resetPassword(u.id, u.email)} className="text-xs text-[#6B7280] hover:text-red-600 hover:underline">PW-Reset</button>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
